/** Shared shell + card chrome for welcome course/level panels (home “Join thousands” stat-card style). */

export const DASHBOARD_COURSE_CARD_SHELL =
  'clay-card group relative overflow-hidden border border-sky-100/70 bg-white/98 p-4 ps-5 transition-shadow hover:shadow-md dark:border-sky-900/35 dark:bg-gray-900/85 sm:p-5 sm:ps-6';

export const DASHBOARD_COURSE_ACCENT_BAR =
  'pointer-events-none absolute bottom-0 start-0 top-0 w-1.5 bg-gradient-to-b from-sky-300 to-blue-400 dark:from-sky-500/70 dark:to-blue-500/70';

export const DASHBOARD_COURSE_WATERMARK =
  'pointer-events-none absolute h-14 w-14 text-sky-400 opacity-[0.07] transition-opacity group-hover:opacity-[0.11] dark:text-sky-300 dark:opacity-[0.1] dark:group-hover:opacity-[0.15] sm:h-16 sm:w-16';

export const DASHBOARD_LEVEL_CARD_SHELL =
  'group relative overflow-hidden rounded-xl border border-emerald-100/70 bg-white/98 p-2.5 ps-4 shadow-sm transition-shadow hover:shadow-md dark:border-emerald-900/30 dark:bg-gray-900/85 sm:ps-5';

export const DASHBOARD_LEVEL_ACCENT_BAR =
  'pointer-events-none absolute bottom-0 start-0 top-0 w-1 bg-gradient-to-b from-emerald-300 to-teal-400 dark:from-emerald-500/65 dark:to-teal-500/65';

export const DASHBOARD_LEVEL_WATERMARK =
  'pointer-events-none absolute h-11 w-11 text-emerald-400 opacity-[0.07] transition-opacity group-hover:opacity-[0.11] dark:text-emerald-300 dark:opacity-[0.09] sm:h-12 sm:w-12';

/** FA: top-left; EN: top-right (physical corners). */
export function dashboardPanelWatermarkClass(isRtl: boolean): string {
  return isRtl ? '-left-1.5 -top-1.5 sm:-left-2 sm:-top-2' : '-right-1.5 -top-1.5 sm:-right-2 sm:-top-2';
}

/** Book-card / hero watermark corner — matches learner dashboard hero. */
export function dashboardBookCardWatermarkCornerClass(isRtl: boolean): string {
  return isRtl ? '-left-3 -top-3 sm:-left-3.5 sm:-top-3.5' : '-right-3 -top-3 sm:-right-3.5 sm:-top-3.5';
}

export const DASHBOARD_BOOK_CARD_WATERMARK =
  'pointer-events-none absolute h-[4.75rem] w-[4.75rem] opacity-[0.12] transition-opacity group-hover:opacity-[0.16] dark:opacity-[0.08] dark:group-hover:opacity-[0.12] sm:h-20 sm:w-20';

/** Colored start-edge stripe on learner dashboard book cards (right in FA, left in EN). */
export const DASHBOARD_BOOK_CARD_EDGE_ACCENT =
  'relative z-[1] flex w-1.5 shrink-0 flex-col items-center justify-center self-stretch sm:w-2';

/** Light panel fill — matches learner dashboard hero, per section theme. */
export const DASHBOARD_WELCOME_CARD_SHELL_BG =
  'bg-gradient-to-br from-amber-50/80 via-orange-50/45 to-amber-50/30 dark:from-amber-950/25 dark:via-gray-900/92 dark:to-orange-950/18';

export const DASHBOARD_WELCOME_CARD_SHELL_RADIAL_A =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(251,191,36,0.14),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(180,83,9,0.1),transparent_58%)]';

export const DASHBOARD_WELCOME_CARD_SHELL_RADIAL_B =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(253,186,116,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(146,64,14,0.08),transparent_55%)]';

export const DASHBOARD_QUICK_ACTIONS_CARD_SHELL_BG =
  'bg-gradient-to-br from-cyan-50/80 via-teal-50/45 to-sky-50/30 dark:from-cyan-950/25 dark:via-gray-900/92 dark:to-sky-950/18';

export const DASHBOARD_QUICK_ACTIONS_CARD_SHELL_RADIAL_A =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(34,211,238,0.14),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(6,182,212,0.1),transparent_58%)]';

export const DASHBOARD_QUICK_ACTIONS_CARD_SHELL_RADIAL_B =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(56,189,248,0.11),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(14,116,144,0.08),transparent_55%)]';

export const DASHBOARD_LIBRARY_CARD_SHELL_BG =
  'bg-gradient-to-br from-sky-50/80 via-cyan-50/45 to-sky-50/30 dark:from-sky-950/25 dark:via-gray-900/92 dark:to-cyan-950/18';

export const DASHBOARD_LIBRARY_CARD_SHELL_RADIAL_A =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(56,189,248,0.14),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(14,165,233,0.1),transparent_58%)]';

export const DASHBOARD_LIBRARY_CARD_SHELL_RADIAL_B =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(125,211,252,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(7,89,133,0.08),transparent_55%)]';

export const DASHBOARD_ACHIEVEMENTS_CARD_SHELL_BG =
  'bg-gradient-to-br from-rose-50/80 via-pink-50/45 to-rose-50/30 dark:from-rose-950/25 dark:via-gray-900/92 dark:to-pink-950/18';

export const DASHBOARD_ACHIEVEMENTS_CARD_SHELL_RADIAL_A =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(251,113,133,0.14),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(190,18,60,0.1),transparent_58%)]';

export const DASHBOARD_ACHIEVEMENTS_CARD_SHELL_RADIAL_B =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(244,114,182,0.11),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(159,18,57,0.08),transparent_55%)]';

export const DASHBOARD_XP_CARD_SHELL_BG =
  'bg-gradient-to-br from-emerald-50/80 via-teal-50/45 to-cyan-50/30 dark:from-emerald-950/25 dark:via-gray-900/92 dark:to-teal-950/18';

export const DASHBOARD_XP_CARD_SHELL_RADIAL_A =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(52,211,153,0.14),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(16,185,129,0.1),transparent_58%)]';

export const DASHBOARD_XP_CARD_SHELL_RADIAL_B =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(45,212,191,0.11),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(15,118,110,0.08),transparent_55%)]';

export const DASHBOARD_LEARNING_STATS_CARD_SHELL_BG =
  'bg-gradient-to-br from-fuchsia-50/80 via-violet-50/45 to-indigo-50/30 dark:from-fuchsia-950/25 dark:via-gray-900/92 dark:to-violet-950/18';

export const DASHBOARD_LEARNING_STATS_CARD_SHELL_RADIAL_A =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(217,70,239,0.14),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(192,38,211,0.1),transparent_58%)]';

export const DASHBOARD_LEARNING_STATS_CARD_SHELL_RADIAL_B =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(167,139,250,0.11),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(109,40,217,0.08),transparent_55%)]';
